/**
 * SA-1127: a triage desk's kind, waiting and mailbox filters behind one
 * Filters button (R2), and the filters that are on as removable chips on
 * the toolbar's second row. Replaces SA-1091's FilterChipRow: the same
 * mailbox list (the page passes `useInboxFilterChips`' answer), the same
 * store selections, and the same rule that nothing renders without a router
 * mailbox kind. A chip toggles one value per group, as the row's did.
 */
import type { ReactNode } from 'react'
import { SlidersHorizontal, X } from 'lucide-react'
import { Badge, Button, Popover, PopoverContent, PopoverTrigger, Tip } from '@/components/ui'
import type { Mailbox } from '@/services/mailboxes'
import { useInboxStore } from '@/stores/inboxStore'
import { cn } from '@/utils'
import { CHIP_ON_CLASS, FilterChip } from './FilterChip'
import {
  ACTIVE_FILTERS_LABEL, CLEAR_ALL_LABEL, CLEAR_LABEL, FILTERS_LABEL, FLAG_CHIPS, GROUP_LABELS,
  activeChipText, activeFilters, alsoKnownAsText, filtersName, kindChips, mailboxChips, removeChipName,
  type ActiveFilter,
} from './filterChips'
import { tourAnchor } from '@/components/tour/tourAnchor'

function useTriageSelections() {
  const kindFilter = useInboxStore((s) => s.kindFilter)
  const flagFilter = useInboxStore((s) => s.flagFilter)
  const mailboxFilter = useInboxStore((s) => s.mailboxFilter)
  const setKindFilter = useInboxStore((s) => s.setKindFilter)
  const setFlagFilter = useInboxStore((s) => s.setFlagFilter)
  const setMailboxFilter = useInboxStore((s) => s.setMailboxFilter)
  const clearTriageFilters = useInboxStore((s) => s.clearTriageFilters)
  return { kindFilter, flagFilter, mailboxFilter, setKindFilter, setFlagFilter, setMailboxFilter, clearTriageFilters }
}

function MenuGroup({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div role="group" aria-label={label}>
      <p aria-hidden="true" className="mb-2 text-[10.5px] font-semibold uppercase tracking-wide text-muted-foreground">{label}</p>
      <div className="flex flex-wrap gap-1.5">{children}</div>
    </div>
  )
}

export function TriageFiltersMenu({ mailboxes }: { mailboxes: Mailbox[] | undefined }) {
  const s = useTriageSelections()
  const kinds = mailboxes ? kindChips(mailboxes) : []
  if (kinds.length === 0) return null
  const boxes = mailboxChips(mailboxes ?? [])
  const active = activeFilters(mailboxes ?? [], { kind: s.kindFilter, flag: s.flagFilter, mailbox: s.mailboxFilter })

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="h-8 text-xs" aria-label={filtersName(active.length)} {...tourAnchor('inbox.toolbar.filters')}>
          <SlidersHorizontal className="size-3.5" aria-hidden="true" />
          {FILTERS_LABEL}
          {active.length > 0 && <Badge variant="neutral" className="px-1.5 py-0 text-[10px]">{active.length}</Badge>}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="w-80 space-y-3.5"
        aria-label={FILTERS_LABEL}
        tabIndex={-1}
        onOpenAutoFocus={(e) => {
          // Focus the menu itself, not its first chip: a kind chip's tooltip opens on focus and would swallow the first Escape.
          e.preventDefault()
          ;(e.currentTarget as HTMLElement).focus()
        }}
      >
        <MenuGroup label={GROUP_LABELS.kind}>
          {kinds.map((chip) => {
            const node = (
              <FilterChip
                key={chip.id}
                on={s.kindFilter === chip.id}
                onClick={() => s.setKindFilter(s.kindFilter === chip.id ? null : chip.id)}
              >
                {chip.label}
              </FilterChip>
            )
            const describe = alsoKnownAsText(chip)
            return describe ? <Tip key={chip.id} text={describe}>{node}</Tip> : node
          })}
        </MenuGroup>
        <MenuGroup label={GROUP_LABELS.flag}>
          {FLAG_CHIPS.map((chip) => (
            <FilterChip
              key={chip.id}
              on={s.flagFilter === chip.id}
              onClick={() => s.setFlagFilter(s.flagFilter === chip.id ? null : chip.id)}
            >
              {chip.label}
            </FilterChip>
          ))}
        </MenuGroup>
        {boxes.length > 0 && (
          <MenuGroup label={GROUP_LABELS.mailbox}>
            {boxes.map((chip) => (
              <FilterChip
                key={chip.id}
                on={s.mailboxFilter === chip.id}
                onClick={() => s.setMailboxFilter(s.mailboxFilter === chip.id ? null : chip.id)}
              >
                {chip.label}
              </FilterChip>
            ))}
          </MenuGroup>
        )}
        {active.length > 0 && (
          <div className="flex justify-end">
            <Button variant="ghost" size="sm" className="h-7 text-xs text-muted-foreground" onClick={s.clearTriageFilters}>
              {CLEAR_ALL_LABEL}
            </Button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  )
}

export function ActiveTriageFilters({ mailboxes }: { mailboxes: Mailbox[] | undefined }) {
  const s = useTriageSelections()
  if (!mailboxes || kindChips(mailboxes).length === 0) return null
  const active = activeFilters(mailboxes, { kind: s.kindFilter, flag: s.flagFilter, mailbox: s.mailboxFilter })
  if (active.length === 0) return null
  const clearOne = (filter: ActiveFilter) => {
    if (filter.group === 'kind') s.setKindFilter(null)
    else if (filter.group === 'flag') s.setFlagFilter(null)
    else s.setMailboxFilter(null)
  }
  return (
    <>
      <span aria-hidden="true" className="mx-1 h-5 w-px bg-border" />
      <div role="group" aria-label={ACTIVE_FILTERS_LABEL} className="flex flex-wrap items-center gap-1.5">
        {active.map((filter) => (
          <span
            key={filter.group}
            className={cn('inline-flex h-7 items-center gap-0.5 rounded-full border bg-muted pl-2.5 pr-1 text-xs font-medium', CHIP_ON_CLASS)}
          >
            {activeChipText(filter.group, filter.label)}
            <button
              type="button"
              aria-label={removeChipName(filter.group, filter.label)}
              onClick={() => clearOne(filter)}
              className="inline-flex size-5 items-center justify-center rounded-full hover:bg-accent"
            >
              <X className="size-3" aria-hidden="true" />
            </button>
          </span>
        ))}
        <button
          type="button"
          onClick={s.clearTriageFilters}
          className="px-1 text-xs text-muted-foreground underline underline-offset-2 hover:text-foreground"
        >
          {CLEAR_LABEL}
        </button>
      </div>
    </>
  )
}
